import React, { useId } from 'react';
import { StatusBadge } from '@/components/ui';
import { visibilityBadgeLabel } from '@/utils/recipientVisibility';
import type { SearchVisibility } from '@/utils/searchVisibility';

type Props = {
  value: SearchVisibility;
  onChange?: (value: SearchVisibility) => void;
  editable: boolean;
  showWorkspaceOption: boolean;
  helperText?: string;
};

function VisibilityRadio({
  name,
  value,
  current,
  onChange,
  title,
  hint,
}: {
  name: string;
  value: SearchVisibility;
  current: SearchVisibility;
  onChange: (value: SearchVisibility) => void;
  title: string;
  hint: string;
}) {
  const inputId = useId();
  return (
    <label className="radio-card radio-card--option" htmlFor={inputId}>
      <input
        id={inputId}
        type="radio"
        name={name}
        checked={current === value}
        onChange={() => onChange(value)}
      />
      <span className="radio-card__body">
        <span className="radio-card__title">{title}</span>
        <span className="radio-card__hint muted">{hint}</span>
      </span>
    </label>
  );
}

export default function SearchVisibilityPanel({
  value,
  onChange,
  editable,
  showWorkspaceOption,
  helperText,
}: Props) {
  const groupName = useId();

  if (!editable) {
    return (
      <div className="search-visibility-panel">
        <h4 className="search-share-panel__title">Visibility</h4>
        <StatusBadge variant={value === 'workspace' ? 'info' : 'neutral'}>
          {visibilityBadgeLabel(value)}
        </StatusBadge>
        <p className="muted search-share-panel__hint">
          {value === 'workspace'
            ? 'Visible to workspace members.'
            : 'Only the owner and workspace admins can see this search.'}
        </p>
      </div>
    );
  }

  return (
    <div className="search-visibility-panel">
      <h4 className="search-share-panel__title">Visibility</h4>
      <div className="radio-card-group">
        <VisibilityRadio
          name={groupName}
          value="private"
          current={value}
          onChange={(next) => onChange?.(next)}
          title="Private"
          hint="Only me and workspace admins"
        />
        {showWorkspaceOption ? (
          <VisibilityRadio
            name={groupName}
            value="workspace"
            current={value}
            onChange={(next) => onChange?.(next)}
            title="Workspace"
            hint="Visible to workspace members"
          />
        ) : null}
      </div>
      {helperText ? <p className="muted search-share-panel__hint">{helperText}</p> : null}
      {!showWorkspaceOption ? (
        <p className="muted search-share-panel__hint">
          Your role can only create private searches in this workspace.
        </p>
      ) : null}
    </div>
  );
}
